import Env from "@/config/environment";
import { fetchServer } from "@/features/utils/fetch-server";
import i18n from "@/features/utils/i18n";
import { useCallback, useEffect, useRef, useState } from "react";

export type WebAudioStatus = "idle" | "connecting" | "live" | "error";
// The visual reads this each animation frame to pick a behaviour. The four cases are
// the turn-taking ones, kept apart on purpose: nothing heard, VAD hearing the caller,
// the turn handed to the LLM, and the agent actually speaking.
export type VoicePhase = "idle" | "listening" | "thinking" | "speaking";

/**
 * What the backend tells us about the turn (`{type:"state", …}` text frames on the audio
 * socket). Only the server knows these: inbound audio alone cannot say whether what is
 * playing is her voice or the ambiance, which flows for the whole call.
 */
interface ServerVoiceState {
  vad: boolean;
  pending: boolean;
  agent: boolean;
}

const EARCONS = { start: "/sounds/voice-start.wav", stop: "/sounds/voice-stop.wav" };

// Same convention as the message notifications: ?sound=0 silences an embed.
const soundEnabled = () =>
  new URLSearchParams(window.location.search).get("sound") !== "0";

const playEarcon = (url: string) => {
  if (!soundEnabled()) return;
  try {
    const audio = new Audio(url);
    audio.volume = 0.45;
    void audio.play().catch(() => {
      /* autoplay policy, or the tab went away */
    });
  } catch {
    /* no audio element available */
  }
};

interface WebAudioConnection {
  grant: string;
  expires_in: number;
  ws_path: string;
  sample_rate: number;
}

// One frame of captured audio, in milliseconds. The whole server pipeline is built on 20ms
// frames (the VAD batches 6 of them, the STT pre-roll buffer counts them, the cleaner's
// zero-run cut is bounded per frame), so posting a render quantum as it comes (128 input
// samples — ~43 at 16kHz, under 3ms) left every one of those working on a fraction of the
// audio it expects, and put ~375 messages/s on the socket.
const FRAME_MS = 20;

// AudioWorklet that resamples the mic to 16kHz and posts Int16 PCM frames to the main
// thread. Inlined as a Blob so it needs no separate build entry. `sampleRate` is the
// AudioContext rate (a worklet global); we resample it down to `targetRate` and emit fixed
// FRAME_MS frames.
const CAPTURE_WORKLET = `
const FRAME_MS = ${FRAME_MS};

class CaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const opts = options.processorOptions || {};
    this.targetRate = opts.targetRate || 16000;
    this.ratio = sampleRate / this.targetRate;
    this.buf = [];
    this.pos = 0;
    this.frame = new Int16Array(Math.round((this.targetRate * FRAME_MS) / 1000));
    this.filled = 0;
  }

  // One output sample, averaged over the whole input window it stands for. Picking a
  // single (interpolated) point instead aliases badly when decimating — at 48kHz -> 16kHz
  // everything above 8kHz folds back into the speech band, and that is what the STT would
  // have to transcribe.
  windowAverage(from, to) {
    const a = Math.max(0, Math.floor(from));
    const b = Math.min(this.buf.length, Math.max(a + 1, Math.ceil(to)));
    let sum = 0;
    for (let i = a; i < b; i++) sum += this.buf[i];
    return sum / (b - a);
  }

  emit(value) {
    const v = Math.max(-1, Math.min(1, value));
    this.frame[this.filled++] = v < 0 ? v * 0x8000 : v * 0x7fff;
    if (this.filled < this.frame.length) return;
    this.filled = 0;
    const out = new Int16Array(this.frame); // copy — the frame buffer is reused
    this.port.postMessage(out.buffer, [out.buffer]);
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const ch = input[0];
    for (let i = 0; i < ch.length; i++) this.buf.push(ch[i]);
    while (this.pos + this.ratio <= this.buf.length) {
      this.emit(this.windowAverage(this.pos, this.pos + this.ratio));
      this.pos += this.ratio;
    }
    const consumed = Math.floor(this.pos);
    if (consumed > 0) { this.buf.splice(0, consumed); this.pos -= consumed; }
    return true;
  }
}
registerProcessor('capture-processor', CaptureProcessor);
`;

const energyOf = (analyser: AnalyserNode | null, buf: Uint8Array): number => {
  if (!analyser) return 0;
  analyser.getByteFrequencyData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) sum += buf[i];
  return sum / (buf.length * 255);
};

/**
 * Browser side of "call here", pure WebSocket (no SIP/WebRTC). Asks the backend for a
 * single-use grant, opens `wss://…/ws/audio?grant=…`, captures the mic as 16kHz PCM via an
 * AudioWorklet and streams it up, and plays the agent's 16kHz PCM back with scheduled
 * AudioBuffer sources (the browser resamples to the context rate). Exposes the agent
 * AnalyserNode and a `phase` ref for the voice visual.
 */
export function useWebAudio(basePath: string | undefined) {
  const [status, setStatus] = useState<WebAudioStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const captureNodeRef = useRef<AudioWorkletNode | null>(null);
  const playCursorRef = useRef(0); // next scheduled playback time (ctx clock)

  // For the visual.
  const agentAnalyserRef = useRef<AnalyserNode | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const phaseRef = useRef<VoicePhase>("idle");
  // Mirrored in state for React-land (the typing indicator); the ref is what the
  // animation frame reads.
  const [phase, setPhase] = useState<VoicePhase>("idle");
  const serverStateRef = useRef<ServerVoiceState>({
    vad: false,
    pending: false,
    agent: false,
  });
  // An older backend sends nothing; then we fall back to reading the energies.
  const hasServerStateRef = useRef(false);
  const wasLiveRef = useRef(false);

  const stop = useCallback(async () => {
    if (wasLiveRef.current) {
      wasLiveRef.current = false;
      playEarcon(EARCONS.stop);
    }
    const ws = wsRef.current;
    wsRef.current = null;
    try {
      ws?.close();
    } catch {
      /* already gone */
    }
    captureNodeRef.current?.disconnect();
    captureNodeRef.current = null;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    agentAnalyserRef.current = null;
    micAnalyserRef.current = null;
    phaseRef.current = "idle";
    setPhase("idle");
    serverStateRef.current = { vad: false, pending: false, agent: false };
    hasServerStateRef.current = false;
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx) {
      try {
        await ctx.close();
      } catch {
        /* already closed */
      }
    }
    if (basePath) {
      try {
        await fetchServer(`${basePath}/audio/stop`, {
          method: "POST",
          credentials: "include",
        });
      } catch {
        /* best effort */
      }
    }
    setStatus("idle");
  }, [basePath]);

  const start = useCallback(async () => {
    if (!basePath || status === "connecting" || status === "live") return;
    setError(null);
    setStatus("connecting");
    phaseRef.current = "idle";
    setPhase("idle");
    serverStateRef.current = { vad: false, pending: false, agent: false };
    hasServerStateRef.current = false;
    try {
      const res = await fetchServer(`${basePath}/audio/start`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(i18n.t("chat.audio_start_failed", { ns: "public" }));
      const conn: WebAudioConnection = await res.json();

      // Mic first — a denied permission should fail before we open the socket.
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      micStreamRef.current = micStream;

      // Ask for the context to run at the wire rate: the browser then resamples the mic
      // with a proper filter instead of leaving it to the worklet, and the agent's 16kHz
      // playback buffers need no conversion either. Browsers that refuse a forced rate
      // throw — the worklet still resamples, it just does the work itself.
      let ctx: AudioContext;
      try {
        ctx = new AudioContext({ sampleRate: conn.sample_rate });
      } catch {
        ctx = new AudioContext();
      }
      await ctx.resume();
      ctxRef.current = ctx;

      // Capture graph: mic → analyser (for the visual) + worklet (→ WS). The worklet is
      // pulled by a muted gain so it runs without echoing the mic to the speakers.
      const micSource = ctx.createMediaStreamSource(micStream);
      const micAnalyser = ctx.createAnalyser();
      micAnalyser.fftSize = 512;
      micSource.connect(micAnalyser);
      micAnalyserRef.current = micAnalyser;

      const blobUrl = URL.createObjectURL(
        new Blob([CAPTURE_WORKLET], { type: "application/javascript" }),
      );
      await ctx.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);
      const captureNode = new AudioWorkletNode(ctx, "capture-processor", {
        processorOptions: { targetRate: conn.sample_rate },
      });
      captureNodeRef.current = captureNode;
      micSource.connect(captureNode);
      const mute = ctx.createGain();
      mute.gain.value = 0;
      captureNode.connect(mute).connect(ctx.destination);

      // Playback graph: scheduled AudioBuffers → agent analyser (for the visual) → speakers.
      const agentAnalyser = ctx.createAnalyser();
      agentAnalyser.fftSize = 2048;
      agentAnalyser.smoothingTimeConstant = 0.6;
      agentAnalyser.connect(ctx.destination);
      agentAnalyserRef.current = agentAnalyser;
      playCursorRef.current = 0;

      const wsUrl =
        Env.server.replace(/^http/, "ws").replace(/\/$/, "") +
        conn.ws_path +
        "?grant=" +
        encodeURIComponent(conn.grant);
      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      captureNode.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(e.data);
      };

      ws.onmessage = (e: MessageEvent) => {
        if (typeof e.data === "string") {
          try {
            const msg = JSON.parse(e.data);
            if (msg?.type === "state") {
              hasServerStateRef.current = true;
              serverStateRef.current = {
                vad: !!msg.vad,
                pending: !!msg.pending,
                agent: !!msg.agent,
              };
            }
          } catch {
            /* ignore malformed control frames */
          }
          return;
        }
        if (!(e.data instanceof ArrayBuffer)) return;
        const pcm = new Int16Array(e.data);
        const f32 = new Float32Array(pcm.length);
        for (let i = 0; i < pcm.length; i++) f32[i] = pcm[i] / 0x8000;
        const buffer = ctx.createBuffer(1, f32.length, conn.sample_rate);
        buffer.copyToChannel(f32, 0);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(agentAnalyser);
        // Small jitter buffer; keep chunks back-to-back on the context clock.
        const now = ctx.currentTime;
        if (playCursorRef.current < now + 0.06) playCursorRef.current = now + 0.06;
        src.start(playCursorRef.current);
        playCursorRef.current += buffer.duration;
      };

      ws.onclose = () => {
        if (wsRef.current === ws) void stop();
      };
      ws.onerror = () => {
        if (wsRef.current === ws) {
          setError(i18n.t("chat.audio_lost", { ns: "public" }));
          setStatus("error");
          void stop();
          setStatus("error");
        }
      };
      ws.onopen = () => {
        wasLiveRef.current = true;
        playEarcon(EARCONS.start);
        setStatus("live");
      };
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : i18n.t("chat.audio_error", { ns: "public" }),
      );
      setStatus("error");
      await stop();
      setStatus("error");
    }
  }, [basePath, status, stop]);

  // The phase the visual draws. The backend is authoritative — it sends vad / pending /
  // agent over the socket — and the energies are only a fallback for a server that
  // doesn't (and for nothing else: the ambiance alone used to read as "she is speaking").
  useEffect(() => {
    if (status !== "live") return;
    const micBuf = new Uint8Array(256);
    const agentBuf = new Uint8Array(1024);
    let lastUserSpokeAt = 0;
    let raf = 0;
    const tick = () => {
      let next: VoicePhase;
      if (hasServerStateRef.current) {
        const st = serverStateRef.current;
        // `agent` frames the whole agent turn: the band levels then carry the detail,
        // including the short silences inside a sentence.
        next = st.agent
          ? "speaking"
          : st.pending
            ? "thinking"
            : st.vad
              ? "listening"
              : "idle";
      } else {
        const mic = energyOf(micAnalyserRef.current, micBuf);
        const agent = energyOf(agentAnalyserRef.current, agentBuf);
        const now = performance.now();
        if (mic > 0.03) lastUserSpokeAt = now;
        if (agent > 0.02) next = "speaking";
        else if (mic > 0.03) next = "listening";
        else if (now - lastUserSpokeAt < 4000) next = "thinking";
        else next = "idle";
      }
      if (next !== phaseRef.current) {
        phaseRef.current = next;
        setPhase(next);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  useEffect(() => {
    return () => {
      void stop();
    };
  }, [stop]);

  const toggle = useCallback(() => {
    if (status === "live" || status === "connecting") return stop();
    return start();
  }, [status, start, stop]);

  // Send a message typed while on the audio leg over the SAME WebSocket, so it reaches the
  // instance running the pipeline and barges in on the agent (cut audio → new spoken answer)
  // — rather than the REST path, which would only produce a silent text reply.
  const sendText = useCallback((text: string) => {
    const ws = wsRef.current;
    if (!text.trim() || !ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({ type: "text", value: text }));
    return true;
  }, []);

  return {
    status,
    error,
    start,
    stop,
    toggle,
    sendText,
    agentAnalyserRef,
    micAnalyserRef,
    phaseRef,
    phase,
    /** The agent is formulating: drives the chat's own typing indicator. */
    isThinking: phase === "thinking",
  };
}
